import { computed, Ref, ref } from 'vue';
import { alphabetize } from './alphabetize';

export const useTableSorting = <T>(data: Ref<T[]>, defaultSortKey: keyof T, defaultSortDirection = 1) => {
  const sortDirection = ref(defaultSortDirection);
  const sortKey = ref(defaultSortKey) as Ref<keyof T>;

  const getNestedValue = (obj: any, path: string) => {
    return path.split('.').reduce((prev: any, curr: any) => prev && prev[curr], obj);
  };

  const sortNestedData = (key: keyof T | string, direction: number) => {
    return [...data.value].sort((a, b) => {
      const aValue = getNestedValue(a, key as string);
      const bValue = getNestedValue(b, key as string);
      if (aValue < bValue) {
        return -1 * direction;
      }
      if (aValue > bValue) {
        return 1 * direction;
      }
      return 0;
    });
  };

  const sortedData = computed(() => {
    if (!data || !sortKey.value) {
      return [];
    }
    const currentSortKey = sortDirection.value === 0 ? defaultSortKey : sortKey.value;
    const currentSortDirection = sortDirection.value === 0 ? defaultSortDirection : sortDirection.value;

    if (typeof sortKey.value === 'string' && sortKey.value.includes('.')) {
      return sortNestedData(currentSortKey, currentSortDirection);
    }
    return alphabetize<T>(data.value, currentSortKey as keyof T, currentSortDirection);
  });

  const progressSortDirectionState = () => {
    switch (sortDirection.value) {
      case 0:
        sortDirection.value = 1;
        break;
      case 1:
        sortDirection.value = -1;
        break;
      case -1:
        sortDirection.value = 0;
        break;
      default:
        sortDirection.value = 1;
    }
  };

  const sortBy = (key: keyof T) => {
    if (sortKey.value === key) {
      progressSortDirectionState();
      return;
    }

    sortKey.value = key;
    sortDirection.value = 1;
  };

  const getSortState = (key: keyof T) => {
    if (sortKey.value !== key) return 'swap_vert';
    if (sortDirection.value === 1) return 'expand_less';
    if (sortDirection.value === -1) return 'expand_more';

    return 'swap_vert';
  };

  return {
    sortedData,
    sortBy,
    getSortState
  };
};
